import React, { useEffect, useRef, useState, useCallback } from "react";
import { createConvergenceScene, CONVERGENCE_DEFAULTS } from "./convergenceScene";
import InstrumentOverlay from "../InstrumentOverlay";

// Rest distance between the two objects in world units.
const REST_DISTANCE_UNIT = Math.sqrt(1.35 * 1.35 + 0.22 * 0.22 + 1.20 * 1.20);

/**
 * Convergence · isolated prototype.
 *
 * Route: /lab/convergence
 * Frame 002 stays untouched at /lab/frame-002.
 */

export default function ConvergenceLab() {
  const containerRef = useRef(null);
  const sceneApiRef = useRef(null);
  const [params, setParams] = useState({ ...CONVERGENCE_DEFAULTS });
  const [uiHidden, setUiHidden] = useState(false);
  const [instrumentVisible, setInstrumentVisible] = useState(false);
  const [progress, setProgress] = useState({ state: "idle", phaseT: 0, phaseDuration: 0, phaseFraction: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const api = createConvergenceScene({
      container,
      initialParams: params,
      onProgress: (p) => setProgress(p),
    });
    sceneApiRef.current = api;
    return () => { api.dispose(); sceneApiRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sceneApiRef.current) sceneApiRef.current.setParams(params);
  }, [params]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (e.key === "h" || e.key === "H") setUiHidden((v) => !v);
      if (e.key === "i" || e.key === "I") setInstrumentVisible((v) => !v);
      if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        if (sceneApiRef.current) sceneApiRef.current.play();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onChange = useCallback((key, value) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  }, []);

  const play = () => sceneApiRef.current?.play();
  const stop = () => sceneApiRef.current?.stop();
  const skipToPre = () => sceneApiRef.current?.jumpToPreConvergence();
  const skipToEnd = () => sceneApiRef.current?.jumpToConverged();
  const shot = () => sceneApiRef.current?.screenshot({ presetName: "convergence" });

  const phaseLabel = {
    "idle": "idle",
    "withdrawing": "act 1 · withdraw",
    "holding-distance": "act 2 · distance",
    "rotating": "act 3 · rotate",
    "settling": "act 4 · settle",
    "convergence-delay": "act 5a · pause",
    "converging": "act 5b · converging",
    "done": "done",
  }[progress.state] || progress.state;

  return (
    <div data-testid="convergence-lab" style={{ position: "fixed", inset: 0, background: "#000", overflow: "hidden" }}>
      <div ref={containerRef} data-testid="convergence-canvas" style={{ position: "absolute", inset: 0 }} />

      {uiHidden && (
        <button
          onClick={() => setUiHidden(false)}
          data-testid="btn-show-ui"
          style={showUiStyle}
        >show (H)</button>
      )}

      {!uiHidden && (
        <aside data-testid="convergence-panel" style={panelStyle}>
          <header style={headerStyle}>
            <span style={titleStyle}>Convergence · Prototype</span>
            <button onClick={() => setUiHidden(true)} data-testid="btn-hide-ui" style={ghostBtn}>hide (H)</button>
          </header>

          <p style={introStyle}>
            The frozen frame plays first. Convergence begins only after the
            final hold. It should feel like a consequence of coherence, not
            a reward animation.
          </p>

          <SectionHeader title="Playback" />
          <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 6, marginBottom: 6 }}>
            <Btn onClick={play} testId="btn-play" primary>{progress.state === "idle" ? "play (space)" : "restart"}</Btn>
            <Btn onClick={stop} testId="btn-stop">stop</Btn>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <Btn onClick={skipToPre} testId="btn-jump-pre">skip to pause</Btn>
            <Btn onClick={skipToEnd} testId="btn-jump-end">skip to end</Btn>
          </div>

          {/* Phase readout */}
          <div style={{ marginTop: 14, marginBottom: 14 }}>
            <div style={rowStyle}>
              <span style={labelStyle}>Phase</span>
              <span style={{ ...valueStyle, color: "#d9b98a" }}>{phaseLabel}</span>
            </div>
            <div style={{ height: 3, background: "rgba(255,255,255,0.05)", borderRadius: 2, overflow: "hidden", marginTop: 4 }}>
              <div style={{ width: `${Math.round((progress.phaseFraction ?? 0) * 100)}%`, height: "100%", background: "#d9b98a", opacity: 0.75, transition: "width 0.1s linear" }} />
            </div>
          </div>

          <SectionHeader title="Convergence" />
          <p style={{ ...noteStyle, marginBottom: 6, lineHeight: 1.6 }}>
            Objects move toward a shared relational center, but retain
            enough separation to remain distinct. They become a system,
            not a pile.
          </p>
          <Slider
            keyName="convergenceDelay"
            label="Delay"
            note="Quiet pause after settle, before movement begins."
            value={params.convergenceDelay}
            min={0} max={8} step={0.05} unit="s"
            onChange={(v) => onChange("convergenceDelay", v)}
          />
          <Slider
            keyName="convergenceDuration"
            label="Duration"
            note="Seconds for the movement. Smootherstep-eased."
            value={params.convergenceDuration}
            min={4} max={30} step={0.1} unit="s"
            onChange={(v) => onChange("convergenceDuration", v)}
          />
          <Slider
            keyName="convergenceAmount"
            label="Amount"
            note="Fraction of separation to close. Never 1.0."
            value={params.convergenceAmount}
            min={0.0} max={0.5} step={0.005}
            onChange={(v) => onChange("convergenceAmount", v)}
          />

          <SectionHeader title="Actions" />
          <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
            <Btn onClick={shot} testId="btn-screenshot">screenshot + metadata</Btn>
          </div>

          <footer style={footerStyle}>
            The frozen frame teaches the viewer how to see.
            <br />This phase shows what becomes possible once they do.
          </footer>
        </aside>
      )}

      <InstrumentOverlay
        visible={instrumentVisible}
        frame="convergence"
        progress={progress}
        params={params}
        restDistance={REST_DISTANCE_UNIT * (params.objectSeparation ?? 1)}
        hasConvergence={true}
      />
    </div>
  );
}


function SectionHeader({ title }) {
  return <div style={{ color: "#a8a29e", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", marginTop: 12, marginBottom: 6 }}>{title}</div>;
}

function Slider({ keyName, label, note, value, min, max, step, unit, onChange }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={rowStyle}>
        <label style={labelStyle}>{label}</label>
        <span style={valueStyle}>{value.toFixed(step < 1 ? 3 : 0)}{unit ? ` ${unit}` : ""}</span>
      </div>
      {note && <div style={noteStyle}>{note}</div>}
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={`control-${keyName}`}
        style={{ width: "100%", accentColor: "#d9b98a", cursor: "pointer" }}
      />
    </div>
  );
}

function Btn({ children, onClick, testId, primary }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        background: primary ? "rgba(217,185,138,0.14)" : "rgba(255,255,255,0.03)",
        border: `1px solid ${primary ? "rgba(217,185,138,0.5)" : "rgba(255,255,255,0.12)"}`,
        color: primary ? "#f3ead6" : "#d6d3d1",
        fontFamily: "inherit", fontSize: 10, padding: "6px 10px",
        borderRadius: 3, cursor: "pointer", letterSpacing: "0.08em", textAlign: "left",
      }}
    >{children}</button>
  );
}

const panelStyle = {
  position: "fixed", top: 16, right: 16, bottom: 16, width: 320,
  overflowY: "auto",
  background: "rgba(6,6,10,0.82)",
  backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
  border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6,
  padding: "16px 14px 20px", color: "#d6d3d1",
  fontFamily: "ui-monospace, SFMono-Regular, monospace",
  fontSize: 11, lineHeight: 1.5, zIndex: 10,
};
const headerStyle = { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.08)" };
const titleStyle = { letterSpacing: "0.14em", textTransform: "uppercase", color: "#a8a29e", fontSize: 10 };
const introStyle = { margin: "0 0 14px", color: "#a8a29e", fontSize: 10, lineHeight: 1.6, fontStyle: "italic" };
const rowStyle = { display: "flex", justifyContent: "space-between", alignItems: "baseline" };
const labelStyle = { color: "#d6d3d1", fontSize: 10 };
const valueStyle = { color: "#78716c", fontSize: 10, fontFamily: "ui-monospace, SFMono-Regular, monospace" };
const noteStyle = { color: "#57534e", fontSize: 9, fontStyle: "italic", marginTop: 1, marginBottom: 3 };
const ghostBtn = { background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#a8a29e", fontFamily: "inherit", fontSize: 10, padding: "2px 8px", borderRadius: 3, cursor: "pointer", letterSpacing: "0.08em" };
const showUiStyle = { position: "fixed", top: 16, right: 16, background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.5)", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 10, padding: "4px 10px", borderRadius: 3, cursor: "pointer", letterSpacing: "0.12em", zIndex: 10 };
const footerStyle = { marginTop: 20, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)", color: "#57534e", fontStyle: "italic", fontSize: 10, lineHeight: 1.6 };
